import fs from 'fs/promises';
import path from 'path';
import type { Stats } from 'fs';

import type pino from 'pino';
import type { FSWatcher } from 'chokidar';
import type { PersistedJob } from '@coderule/qulite';

import { startWatcher, type WatchEvent } from '../fs/Watcher';
import { buildWatcherIgnored, shouldIncludeFile } from '../rules/RulesCompiler';
import { toPosixRelative, isInsideRoot } from '../fs/PathUtil';
import {
  publishSnapshot,
  computeSnapshot,
  SnapshotStaleError,
} from '../sync/SnapshotProtocol';
import { sendHeartbeat } from '../sync/HeartbeatProtocol';

import { ServiceState } from './State';
import type { BootstrapResult } from './Bootstrap';

function safeParse<T = unknown>(input: string): T | undefined {
  try {
    return JSON.parse(input) as T;
  } catch {
    return undefined;
  }
}

async function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function computeBackoff(attempts: number | undefined): number {
  const base = attempts ?? 0;
  const delay = 1_000 * 2 ** base;
  return Math.min(delay, 60_000);
}

const PUBLISH_MAX_MS = 5 * 60_000;

async function readSymlinkTarget(absPath: string): Promise<string | null> {
  try {
    return await fs.readlink(absPath);
  } catch {
    return null;
  }
}

class SnapshotScheduler {
  private timer: NodeJS.Timeout | null = null;

  constructor(
    private readonly rootId: string,
    private readonly debounceMs: number,
    private readonly outbox: BootstrapResult['outbox'],
    private readonly state: ServiceState,
  ) {}

  trigger(): void {
    this.state.updateChange();
    if (this.timer) {
      clearTimeout(this.timer);
    }
    this.timer = setTimeout(() => {
      this.outbox.enqueueSnapshot(this.rootId);
      this.timer = null;
    }, this.debounceMs);
  }

  cancel(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}

export class ServiceRunner {
  private readonly state = new ServiceState();
  private watcher: FSWatcher | null = null;
  private running = false;
  private readonly fsControlLeaseOwner = `fs-control-${process.pid}-${Date.now()}`;
  private readonly scheduler: SnapshotScheduler;
  private readonly ignoredPredicate: (path: string, stats?: Stats) => boolean;
  private readonly tasks = new Set<Promise<void>>();
  private buffering = false;
  private bufferedEvents: Array<{
    event: WatchEvent;
    absPath: string;
    stats?: Stats;
  }> = [];

  constructor(private readonly runtime: BootstrapResult) {
    this.scheduler = new SnapshotScheduler(
      runtime.config.rootId,
      runtime.config.snapshotDebounceMs,
      runtime.outbox,
      this.state,
    );
    this.ignoredPredicate = buildWatcherIgnored(runtime.rules);
  }

  recordInitialSnapshot(timestamp: number): void {
    this.state.updateSnapshotReady(timestamp);
    this.state.updateChange(timestamp);
    this.state.updateHeartbeat(timestamp);
  }

  async start(): Promise<void> {
    if (this.running) return;
    await this.prepareWatcher(false);
    await this.startLoops();
  }

  async stop(): Promise<void> {
    if (!this.running) return;
    this.running = false;
    this.scheduler.cancel();
    if (this.watcher) {
      await this.watcher.close();
      this.watcher = null;
    }
    await Promise.all([...this.tasks]);
    await this.runtime.hasher.close();
    this.runtime.clients.close();
    this.runtime.db.close();
  }

  getServiceStateSnapshot(): {
    lastChangeAt: number;
    lastSnapshotReadyAt: number;
    lastHeartbeatEnqueuedAt: number;
    watcherReady: boolean;
    buffering: boolean;
  } {
    return {
      lastChangeAt: this.state.lastChangeAt,
      lastSnapshotReadyAt: this.state.lastSnapshotReadyAt,
      lastHeartbeatEnqueuedAt: this.state.lastHeartbeatEnqueuedAt,
      watcherReady: this.watcher !== null,
      buffering: this.buffering,
    };
  }

  private runBackground(fn: () => Promise<void>): void {
    const task = fn();
    this.tasks.add(task);
    task
      .catch((error) => {
        if (this.running) {
          this.runtime.logger.error({ err: error }, 'Background task failed');
        }
      })
      .finally(() => {
        this.tasks.delete(task);
      });
  }

  async prepareWatcher(bufferOnly: boolean): Promise<void> {
    const { rootPath } = this.runtime.config;
    const logger = this.runtime.logger.child({ scope: 'watcher' });
    this.buffering = bufferOnly;
    const { watcher, mode } = await startWatcher({
      rootPath,
      ignored: this.ignoredPredicate,
      logger,
      handlers: {
        onEvent: (event, absPath, stats) => {
          logger.debug({ event, absPath }, 'Watcher event received');
          if (this.buffering) {
            this.bufferedEvents.push({ event, absPath, stats });
            return Promise.resolve();
          }
          return this.handleEvent(event, absPath, stats);
        },
      },
    });
    this.watcher = watcher;
    logger.debug({ watched: watcher.getWatched() }, 'Watcher targets');
    logger.info({ mode, buffering: bufferOnly }, 'File watcher started');
  }

  async enableWatcherProcessing(): Promise<void> {
    if (!this.buffering) {
      return;
    }
    this.buffering = false;
    this.runtime.logger.debug(
      { buffered: this.bufferedEvents.length },
      'Watcher buffering disabled',
    );
    if (this.bufferedEvents.length === 0) return;
    for (const buffered of this.bufferedEvents) {
      await this.handleEvent(buffered.event, buffered.absPath, buffered.stats);
    }
    this.bufferedEvents = [];
  }

  async startLoops(): Promise<void> {
    if (this.running) return;
    this.running = true;
    this.runBackground(() => this.hashLoop());
    this.runBackground(() => this.fsControlLoop());
    this.runBackground(() => this.heartbeatLoop());
    this.runBackground(() => this.requeueLoop());
    this.runtime.logger.debug('Background loops started');
  }

  private async handleEvent(
    event: WatchEvent,
    absPath: string,
    stats?: Stats,
  ): Promise<void> {
    if (!this.running) return;
    const root = this.runtime.config.rootPath;
    const absolute = path.isAbsolute(absPath)
      ? absPath
      : path.join(root, absPath);
    if (!isInsideRoot(root, absolute)) {
      return;
    }

    switch (event) {
      case 'add':
      case 'change':
        await this.handleAddChange(absolute, stats);
        break;
      case 'unlink':
        await this.handleUnlink(absolute);
        break;
      case 'unlinkDir':
        await this.handleUnlinkDir(absolute);
        break;
      case 'addDir':
      default:
        break;
    }
  }

  private async handleAddChange(
    absPath: string,
    _stats?: Stats,
  ): Promise<void> {
    let fileStats: Stats;
    try {
      // Always re-stat on change to avoid stale metadata from chokidar callbacks.
      fileStats = await fs.lstat(absPath);
    } catch (error) {
      this.runtime.logger.warn(
        { err: error, path: absPath },
        'Failed to lstat path',
      );
      return;
    }
    const relPath = toPosixRelative(this.runtime.config.rootPath, absPath);
    if (!shouldIncludeFile(relPath, fileStats, this.runtime.rules)) {
      this.runtime.logger.debug({ relPath }, 'Watcher event ignored by rules');
      return;
    }
    // Skip zero-length files (empty files cause server delays)
    if (fileStats.size === 0) {
      this.runtime.logger.debug(
        { relPath },
        'Watcher skipping zero-length file',
      );
      return;
    }
    const isSymlink = fileStats.isSymbolicLink();
    const target = isSymlink ? await readSymlinkTarget(absPath) : null;
    const state = this.runtime.filesRepo.upsertFromStat({
      relPath,
      displayPath: absPath,
      stats: fileStats,
      isSymlink,
      symlinkTarget: target,
    });
    if (state === 'dirty') {
      this.scheduler.trigger();
    }
  }

  private async handleUnlink(absPath: string): Promise<void> {
    const relPath = toPosixRelative(this.runtime.config.rootPath, absPath);
    const changed = this.runtime.filesRepo.markMissing(relPath);
    if (changed > 0) {
      this.scheduler.trigger();
    }
  }

  private async handleUnlinkDir(absPath: string): Promise<void> {
    const relPath = toPosixRelative(this.runtime.config.rootPath, absPath);
    const changed = this.runtime.filesRepo.markMissingByPrefix(relPath);
    if (changed > 0) {
      this.scheduler.trigger();
    }
  }

  private async hashLoop(): Promise<void> {
    while (this.running) {
      const processed = await this.runtime.hasher.processBatch(
        this.runtime.config.hashBatchSize,
      );
      if (!processed) {
        await sleep(500);
      }
    }
  }

  private async fsControlLoop(): Promise<void> {
    const log = this.runtime.logger.child({ scope: 'fs-control-worker' });
    while (this.running) {
      const job = this.runtime.outbox.claimFsControlJob(
        this.fsControlLeaseOwner,
      );
      if (!job) {
        await sleep(this.runtime.config.queuePollIntervalMs);
        continue;
      }

      const payload = job.data
        ? (safeParse<Record<string, unknown>>(job.data) ?? {})
        : {};
      const jobKind =
        job.kind ??
        (typeof payload.kind === 'string' ? payload.kind : undefined);

      if (!jobKind) {
        log.warn(
          { jobId: job.id },
          'fs_control job missing kind, acknowledging',
        );
        this.runtime.outbox.ack(job.id, this.fsControlLeaseOwner);
        continue;
      }

      if (jobKind === 'snapshot') {
        await this.handleSnapshotJob(job, log);
      } else if (jobKind === 'heartbeat') {
        await this.handleHeartbeatJob(job, log);
      } else {
        log.warn({ jobId: job.id, kind: jobKind }, 'Unknown fs_control job');
        this.runtime.outbox.ack(job.id, this.fsControlLeaseOwner);
      }
    }
  }

  private async heartbeatLoop(): Promise<void> {
    const log = this.runtime.logger.child({ scope: 'heartbeat-loop' });
    while (this.running) {
      const now = Date.now();
      const sinceChange = now - this.state.lastChangeAt;
      const sinceHeartbeat = now - this.state.lastHeartbeatEnqueuedAt;
      if (
        sinceChange >= this.runtime.config.heartbeatIntervalMs &&
        sinceHeartbeat >= this.runtime.config.heartbeatIntervalMs
      ) {
        this.runtime.outbox.enqueueHeartbeat(this.runtime.config.rootId);
        this.state.updateHeartbeat(now);
        log.debug('Heartbeat enqueued');
      }
      await sleep(this.runtime.config.heartbeatCheckIntervalMs);
    }
    log.info('Heartbeat loop stopped');
  }

  private async requeueLoop(): Promise<void> {
    while (this.running) {
      const count = this.runtime.outbox.requeueTimedOut();
      if (count > 0) {
        this.runtime.logger.info({ count }, 'Requeued timed-out jobs');
      }
      await sleep(this.runtime.config.heartbeatCheckIntervalMs);
    }
  }

  private async handleSnapshotJob(
    job: PersistedJob,
    log: pino.Logger,
  ): Promise<void> {
    if (
      this.runtime.filesRepo.countByState('dirty') > 0 ||
      this.runtime.filesRepo.countByState('hashing') > 0
    ) {
      const delay = computeBackoff(job.attempts);
      this.runtime.outbox.retry(job.id, this.fsControlLeaseOwner, delay);
      return;
    }

    const publishSeq = this.state.changeSeq;

    const isStale = () =>
      this.state.changeSeq !== publishSeq ||
      this.runtime.filesRepo.countByState('dirty') > 0 ||
      this.runtime.filesRepo.countByState('hashing') > 0;

    try {
      const result = await publishSnapshot(
        this.runtime.config.rootPath,
        this.runtime.filesRepo,
        this.runtime.snapshotsRepo,
        this.runtime.clients.sync,
        log,
        {
          maxAttempts: this.runtime.config.maxSnapshotAttempts,
          uploadChunkSize: this.runtime.config.uploadChunkSize,
          deadlineMs: Date.now() + PUBLISH_MAX_MS,
          isStale,
        },
      );

      const acked = this.runtime.outbox.ack(job.id, this.fsControlLeaseOwner);
      if (!acked) {
        log.warn(
          { jobId: job.id },
          'Snapshot job ack failed (lease may have expired)',
        );
      }

      this.state.updateSnapshotReady(result.createdAt);
      log.info({ snapshotHash: result.snapshotHash }, 'Snapshot job completed');

      // Post-publish dedupe check: ensure no changes were missed
      if (
        this.runtime.filesRepo.countByState('dirty') > 0 ||
        this.runtime.filesRepo.countByState('hashing') > 0
      ) {
        this.runtime.outbox.enqueueSnapshot(this.runtime.config.rootId, 0);
        return;
      }
      const nowHash = computeSnapshot(this.runtime.filesRepo).snapshotHash;
      if (nowHash !== result.snapshotHash) {
        this.runtime.outbox.enqueueSnapshot(this.runtime.config.rootId, 0);
      }
    } catch (error: any) {
      if (error instanceof SnapshotStaleError) {
        log.debug(
          { err: error },
          'Snapshot publish aborted (stale); quick retry',
        );
        this.runtime.outbox.retry(job.id, this.fsControlLeaseOwner, 250);
        return;
      }
      log.warn({ err: error }, 'Snapshot job failed');
      const delay = computeBackoff(job.attempts);
      this.runtime.outbox.retry(job.id, this.fsControlLeaseOwner, delay);
    }
  }

  private async handleHeartbeatJob(
    job: PersistedJob,
    log: pino.Logger,
  ): Promise<void> {
    try {
      await sendHeartbeat(
        this.runtime.config.rootId,
        this.runtime.snapshotsRepo,
        this.runtime.clients.sync,
        log,
      );
      this.runtime.outbox.ack(job.id, this.fsControlLeaseOwner);
      this.state.updateHeartbeat(Date.now());
    } catch (error: any) {
      const delay = computeBackoff(job.attempts);
      this.runtime.outbox.retry(job.id, this.fsControlLeaseOwner, delay);
      log.warn({ err: error }, 'Heartbeat failed; retry scheduled');
    }
  }
}
